import { Alert, Button, CircularProgress } from '@mui/material';
import type { AxiosError } from 'axios';
import { Sparkles } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { createVideoJob } from '../api/generateVideo.api';
import { useImageUploads } from '../hooks/useImageUploads';
import { useModelInputValues } from '../hooks/useModelInputValues';
import { useServerEstimate } from '../hooks/useServerEstimate';
import { cleanValues, hasPricingDimension, missingRequired } from '../inputFieldValues';
import type {
  GenerateVideoApiError,
  GenerateVideoConfig,
  VideoJob,
  VideoModelOption,
  VideoOperation,
} from '../types';
import { estimateJobTokens } from '../utils';
import { EstimateSummary } from './EstimateSummary';
import { ImageInputPicker } from './ImageInputPicker';
import { ModelInputFields } from './ModelInputFields';
import { ModelPicker } from './ModelPicker';
import { OperationTabs } from './OperationTabs';
import { PromptInput } from './PromptInput';
import { VideoSettings } from './VideoSettings';

interface GenerateVideoFormProps {
  config: GenerateVideoConfig;
  onCreated: (job: VideoJob) => void;
}

type SubmitError = { message: string; needsTokens: boolean };

export const GenerateVideoForm: React.FC<GenerateVideoFormProps> = ({ config, onCreated }) => {
  const { t } = useTranslation();
  const firstEnabled =
    config.operations.find((operation) => operation.enabled) ?? config.operations[0];
  const [operation, setOperation] = useState<VideoOperation>(firstEnabled.capabilityCode);
  const operationConfig =
    config.operations.find((item) => item.capabilityCode === operation) ?? firstEnabled;
  const models = operationConfig.models;

  const [selection, setSelection] = useState(() => defaultSelection(models));
  const model = useMemo<VideoModelOption | undefined>(
    () =>
      models.find((item) => item.mode === selection.mode && item.modelCode === selection.modelCode),
    [models, selection]
  );
  const [durationSeconds, setDurationSeconds] = useState(model?.defaultDurationSeconds ?? 0);
  const [aspectRatio, setAspectRatio] = useState(model?.defaultAspectRatio ?? '');
  const inputFields = useMemo(() => model?.inputFields ?? [], [model]);
  const modelInputs = useModelInputValues(
    inputFields,
    `${operation}|${selection.mode}|${selection.modelCode}`
  );
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<SubmitError | null>(null);
  const uploads = useImageUploads(operation);
  // One key per intended submission, so a retried request cannot create a second paid job.
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    setSelection(defaultSelection(models));
    uploads.clear();
    // Reset only when the operation (and so its model list) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation]);

  useEffect(() => {
    if (!model) return;
    setDurationSeconds((current) =>
      model.durationSecondsOptions.includes(current) ? current : model.defaultDurationSeconds
    );
    setAspectRatio((current) =>
      model.aspectRatioOptions.includes(current) ? current : model.defaultAspectRatio
    );
  }, [model]);

  useEffect(() => {
    idempotencyKey.current = crypto.randomUUID();
    setError(null);
  }, [
    operation,
    selection,
    durationSeconds,
    aspectRatio,
    prompt,
    negativePrompt,
    uploads.images,
    modelInputs.values,
  ]);

  const extraInputs = useMemo(
    () => cleanValues(inputFields, modelInputs.values),
    [inputFields, modelInputs.values]
  );
  const serverEstimate = useServerEstimate(Boolean(model) && hasPricingDimension(inputFields), {
    operation,
    mode: selection.mode,
    modelCode: selection.modelCode,
    providerCode: model?.providerCode,
    durationSeconds,
    aspectRatio,
    inputs: extraInputs,
    imageCount: operation === 'image_to_video' ? Math.max(1, uploads.images.length) : undefined,
  });

  if (!model) {
    return <Alert severity="info">{t('services.generateVideo.noModels')}</Alert>;
  }

  const isImageMode = operation === 'image_to_video';
  const promptRequired = !isImageMode || model.requirePrompt;
  const uploadedImages = uploads.images.flatMap((image) =>
    image.status === 'uploaded' && image.uploaded ? [image.uploaded] : []
  );
  const uploading = uploads.images.some((image) => image.status === 'uploading');
  const estimatedTokens = serverEstimate ?? estimateJobTokens(model, durationSeconds);
  const missingInputs = missingRequired(inputFields, modelInputs.values);
  const canSubmit =
    !submitting &&
    !uploading &&
    (!promptRequired || prompt.trim().length > 0) &&
    (!isImageMode || uploadedImages.length > 0) &&
    missingInputs.length === 0 &&
    estimatedTokens <= config.advanceTokens;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const job = await createVideoJob(
        {
          operation,
          mode: model.mode,
          modelCode: model.modelCode,
          providerCode: model.providerCode,
          payload: {
            prompt: prompt.trim() || undefined,
            negativePrompt: negativePrompt.trim() || undefined,
            durationSeconds,
            aspectRatio,
            images: isImageMode
              ? uploadedImages.map((image) => ({ fileAssetPublicId: image.publicId }))
              : undefined,
            inputs: Object.keys(extraInputs).length > 0 ? extraInputs : undefined,
          },
        },
        idempotencyKey.current
      );
      onCreated(job);
      setPrompt('');
      setNegativePrompt('');
      uploads.clear();
      idempotencyKey.current = crypto.randomUUID();
    } catch (caught) {
      const data = (caught as AxiosError<GenerateVideoApiError>).response?.data;
      setError({
        message: data?.message ?? t('services.generateVideo.submitFailed'),
        needsTokens: data?.errorCode === 'ADVANCE_TOKENS_INSUFFICIENT',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) void submit();
      }}
    >
      <OperationTabs operations={config.operations} value={operation} onChange={setOperation} />
      <ModelPicker
        models={models}
        mode={selection.mode}
        modelCode={selection.modelCode}
        onChange={setSelection}
      />
      {isImageMode && (
        <ImageInputPicker
          images={uploads.images}
          maxImages={model.maxInputImages}
          maxImageBytes={operationConfig.maxImageBytes}
          allowedMimeTypes={operationConfig.allowedImageMimeTypes}
          onAdd={uploads.add}
          onRemove={uploads.remove}
        />
      )}
      <PromptInput
        prompt={prompt}
        negativePrompt={negativePrompt}
        maxChars={operationConfig.maxPromptChars}
        required={promptRequired}
        assistContext={{
          operation,
          aspectRatio,
          durationSeconds,
          hasInputImage: isImageMode && uploadedImages.length > 0,
        }}
        onPromptChange={setPrompt}
        onNegativePromptChange={setNegativePrompt}
      />
      <VideoSettings
        durationOptions={model.durationSecondsOptions}
        aspectRatioOptions={model.aspectRatioOptions}
        durationSeconds={durationSeconds}
        aspectRatio={aspectRatio}
        onDurationChange={setDurationSeconds}
        onAspectRatioChange={setAspectRatio}
      />
      {inputFields.length > 0 && (
        <ModelInputFields
          fields={inputFields}
          values={modelInputs.values}
          onChange={modelInputs.setValue}
          disabled={submitting}
        />
      )}
      {missingInputs.length > 0 && (
        <Alert severity="info">
          {t('services.generateVideo.missingInputs', { fields: missingInputs.join(', ') })}
        </Alert>
      )}
      <EstimateSummary estimatedTokens={estimatedTokens} advanceTokens={config.advanceTokens} />
      {error && (
        <Alert
          severity="error"
          action={
            error.needsTokens ? (
              <Button component={Link} to="/subscription" color="inherit" size="small">
                {t('services.generateVideo.buyTokens')}
              </Button>
            ) : undefined
          }
        >
          {error.message}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!canSubmit}
        startIcon={
          submitting ? <CircularProgress size={18} color="inherit" /> : <Sparkles size={18} />
        }
      >
        {t('services.generateVideo.submit')}
      </Button>
    </form>
  );
};

function defaultSelection(models: VideoModelOption[]) {
  const first = models[0];
  return { mode: first?.mode ?? '', modelCode: first?.modelCode ?? '' };
}
